// Store pharmacy data globally for map integration
let allPharmacies = [];
let filteredPharmacies = [];
let markers = [];
let markerCluster = null;
let infoWindow = null;
let mapLoaded = false;
let AdvancedMarkerElement = null;
let PinElement = null;

// Loading overlay functions
function showLoadingOverlay() {
    const overlay = document.getElementById('map-loading-overlay');
    if (overlay) {
        overlay.classList.add('show');
    }
}

function hideLoadingOverlay() {
    const overlay = document.getElementById('map-loading-overlay');
    if (overlay) {
        overlay.classList.remove('show');
    }
}

// Initialize pharmacy functionality when Google Maps is ready
async function initPharmacyMap() {
    // Check if map element exists
    if (!document.getElementById('map')) {
        return;
    }

    // Check if map is initialized
    if (typeof map === 'undefined' || !map) {
        return;
    }

    // Set map loaded flag and configure map
    mapLoaded = true;

    // Add zoom and pan controls
    map.setOptions({
        zoomControl: true,
        zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER
        },
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        fullscreenControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP
        }
    });

    // Load pharmacies immediately
    loadPharmacies();

    setupEventListeners();
}

// Setup all event listeners
function setupEventListeners() {

    // Handle pharmacy suggestion selection
    document.addEventListener('click', function (e) {
        if (e.target.closest('.pharmacy-suggestion')) {
            const suggestion = e.target.closest('.pharmacy-suggestion');
            const input = document.getElementById('map-search');

            // Fill the input with the selected pharmacy name
            input.value = suggestion.dataset.pharmacyName;

            // Hide suggestions
            document.getElementById('pharmacy-suggestions').innerHTML = '';

            // Center map on selected pharmacy if coordinates are available
            const pharmacyId = suggestion.dataset.pharmacyId;
            centerMapOnPharmacy(pharmacyId);
        }
    });

    // Hide suggestions when clicking outside
    document.addEventListener('click', function (e) {
        if (!e.target.closest('.map-search-wrapper') && !e.target.closest('#pharmacy-suggestions')) {
            document.getElementById('pharmacy-suggestions').innerHTML = '';
        }
    });

    // Filter functionality
    const filterToggle = document.getElementById('filter-toggle');
    const filterDropdown = document.getElementById('filter-dropdown');
    const filterClose = document.querySelector('.filter-close');
    const resetFilters = document.getElementById('reset-filters');
    const applyFilters = document.getElementById('apply-filters');

    // Toggle filter dropdown
    filterToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        filterDropdown.classList.toggle('open');
    });

    // Close filter dropdown
    filterClose.addEventListener('click', function () {
        filterDropdown.classList.remove('open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function (e) {
        if (!e.target.closest('.map-filter-wrapper')) {
            filterDropdown.classList.remove('open');
        }
    });

    // Reset filters
    resetFilters.addEventListener('click', function () {
        const checkboxes = filterDropdown.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
        });
        applyFiltersToMap();
    });

    // Apply filters
    applyFilters.addEventListener('click', function () {
        applyFiltersToMap();
        filterDropdown.classList.remove('open');
    });

    // Handle accordion toggle
    document.addEventListener("click", function (e) {
        const toggler = e.target.closest("[data-accordion-toggler]");
        if (!toggler) return;

        const article = toggler.closest("[data-accordion]");
        article.classList.toggle("closed");

        const content = article.querySelector("[data-accordion-content]");
        content.classList.toggle("hidden");
    });
}

function getDistinctFilters(pharmacies) {
    const filtersMap = new Map();
    pharmacies.forEach(pharmacy => {
        pharmacy.filters?.forEach(filter => {
            filtersMap.set(filter.value, filter.text);
        });
    });
    return Array.from(filtersMap, ([value, text]) => ({ value, text }));
}

function renderDynamicFilters(filters) {
    const container = document.getElementById('dynamic-filters');
    if (!container) return;

    const filterHTML = filters.map(filter => `
        <label class="filter-option">
            <input type="checkbox" data-filter-value="${filter.value}">
            <span class="checkmark"></span>
            ${filter.text}
        </label>
    `).join('');

    container.innerHTML = filterHTML;
}

function applyFiltersToMap() {
    // Check if map is loaded before proceeding
    if (!mapLoaded) {
        return;
    }

    // Show loading overlay
    showLoadingOverlay();

    // Get "open" filter
    const openFilter = document.getElementById('filter-open')?.checked || false;

    // Get all checked dynamic filters
    const dynamicFilterCheckboxes = document.querySelectorAll('#dynamic-filters input[type="checkbox"]:checked');
    const selectedFilterValues = Array.from(dynamicFilterCheckboxes).map(cb => cb.dataset.filterValue);

    // Filter pharmacies based on selected criteria
    filteredPharmacies = allPharmacies.filter(pharmacy => {
        // Check open filter
        if (openFilter && !pharmacy.open) {
            return false;
        }

        // Check dynamic filters
        if (selectedFilterValues.length > 0) {
            const hasMatchingFilter = selectedFilterValues.every(filterValue => {
                return pharmacy.filters?.some(f => f.value === filterValue);
            });
            if (!hasMatchingFilter) {
                return false;
            }
        }

        return true;
    });

    // Update markers on the map
    updateMarkersOnMap();

    // Hide loading overlay after a short delay to show the filtering process
    setTimeout(() => {
        hideLoadingOverlay();
    }, 500);
}

async function loadPharmacies() {
    try {
        // Show loading overlay for initial load
        showLoadingOverlay();

        const response = await fetch('/Pharmacy/GetAllPharmacies');
        const result = await response.json();

        allPharmacies = result.data;

        // Render availableFilters
        renderDynamicFilters(result.availableFilters || []);

        // Apply initial filters (all checked by default)
        applyFiltersToMap();

    } catch (error) {
        console.error('Error loading pharmacies:', error);
        // Hide loading overlay on error
        hideLoadingOverlay();
    }
}

function updateMarkersOnMap() {
    // Clear existing markers
    if (markerCluster) {
        markerCluster.clearMarkers();
    }
    markers.forEach(marker => marker.setMap(null));
    markers = [];

    // Create info window (reuse single instance)
    if (!infoWindow) {
        infoWindow = new google.maps.InfoWindow();
    }

    // Filter pharmacies with valid coordinates
    const validPharmacies = filteredPharmacies.filter(
        pharmacy => pharmacy.latitude && pharmacy.longitude
    );

    // Create markers for each pharmacy using regular Marker (AdvancedMarkerElement requires Map ID)
    validPharmacies.forEach(pharmacy => {
        const position = {
            lat: pharmacy.latitude,
            lng: pharmacy.longitude
        };

        const marker = new google.maps.Marker({
            position: position,
            map: map,
            title: pharmacy.name
        });

        // Add click listener to show info window
        const customPanel = document.getElementById("pharmacy-details-panel");

        marker.addListener('click', async function () {
            // Fetch real-time open status
            const statusData = await fetchPharmacyOpenStatus(pharmacy);

            // Merge status data into pharmacy object
            const pharmacyWithStatus = {
                ...pharmacy,
                open: statusData?.open ?? false,
                openHelpText: statusData?.openHelpText ?? '',
                closedHelpText: statusData?.closedHelpText ?? ''
            };

            const popupContent = createPopupContentFromPharmacy(pharmacyWithStatus);
            customPanel.innerHTML = popupContent;
            customPanel.style.display = "block";

            const closeBtn = document.getElementById("close-pharmacy-panel");
            if (closeBtn) {
                closeBtn.addEventListener("click", () => {
                    customPanel.style.display = "none";
                });
            }
        });

        markers.push(marker);
    });

    // Add marker clustering with custom red styling
    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
        // Custom renderer for red clusters that scale with size
        const renderer = {
            render: ({ count, position }, stats) => {
                // Calculate size based on count - larger clusters get bigger
                const baseSize = 40;
                const maxSize = 80;
                const size = Math.min(baseSize + (count / 10) * 5, maxSize);

                // Create red circle SVG
                const svg = `
                    <svg fill="#DC2626" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="${size}" height="${size}">
                        <circle cx="120" cy="120" opacity="1" r="70" />
                        <circle cx="120" cy="120" opacity=".3" r="90" />
                        <circle cx="120" cy="120" opacity=".2" r="110" />
                    </svg>
                `;

                // Create the marker element
                const marker = new google.maps.Marker({
                    position,
                    icon: {
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                        scaledSize: new google.maps.Size(size, size),
                    },
                    label: {
                        text: String(count),
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 'bold',
                    },
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });

                return marker;
            }
        };

        markerCluster = new markerClusterer.MarkerClusterer({
            map,
            markers,
            algorithm: new markerClusterer.SuperClusterAlgorithm({ radius: 100 }),
            renderer: renderer
        });
    }
}

function centerMapOnPharmacy(pharmacyId) {
    const pharmacy = allPharmacies.find(p => p.pharmacyId === parseInt(pharmacyId));
    if (pharmacy && pharmacy.latitude && pharmacy.longitude) {
        const { latitude, longitude } = pharmacy;
        map.panTo({ lat: latitude, lng: longitude });
        map.setZoom(15);

        // Find and click the marker to show info
        const marker = markers.find(m => {
            const pos = m.getPosition();
            return pos && Math.abs(pos.lat() - latitude) < 0.0001 && Math.abs(pos.lng() - longitude) < 0.0001;
        });

        if (marker) {
            google.maps.event.trigger(marker, 'click');
        }
    }
}

async function fetchPharmacyOpenStatus(pharmacy) {
    try {
        const response = await fetch(`/Pharmacy/Autocomplete?q=${encodeURIComponent(pharmacy.name)}&limit=100`);

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.warn('Autocomplete returned non-JSON response');
            return null;
        }

        const results = await response.json();

        // Find matching pharmacy by pharmacyId
        const match = results.find(p => p.pharmacyId === pharmacy.pharmacyId);

        if (match) {
            return {
                open: match.open,
                openHelpText: match.openHelpText,
                closedHelpText: match.closedHelpText
            };
        }

        return null;
    } catch (error) {
        console.error('Error fetching pharmacy status:', error);
        return null;
    }
}

function timeStringToMinutes(timeStr) {
    if (!timeStr) return null;
    // Handle "HH:mm:ss" format
    const parts = timeStr.split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return h * 60 + m;
}

function isPharmacyOpen(pharmacy) {
    if (!pharmacy.openingHours || pharmacy.openingHours.length === 0) {
        return false;
    }

    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

    // Find today's hours
    const todayHours = pharmacy.openingHours.find(
        day => day.date.startsWith(currentDate)
    );

    if (!todayHours) {
        return false;
    }

    // Check if closed (empty openingHours string means closed)
    if (!todayHours.openingHours) {
        return false;
    }

    // Parse the "HH:mm-HH:mm" format
    const timeParts = todayHours.openingHours.split('-');
    if (timeParts.length !== 2) {
        return false;
    }

    // Check if within opening hours
    const openTime = timeStringToMinutes(timeParts[0]);
    const closeTime = timeStringToMinutes(timeParts[1]);

    if (openTime == null || closeTime == null) {
        return false;
    }

    return currentTimeInMinutes >= openTime && currentTimeInMinutes <= closeTime;
}

function getOpeningDetails(pharmacy) {
    const openingHours = pharmacy.openingHours;
    if (!openingHours || openingHours.length === 0) return null;

    const now = new Date();
    const currentDate = now.toISOString().split("T")[0];
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const today = openingHours.find(day => day.date.startsWith(currentDate));

    let todayCloseText = null;
    let nextOpen = null;

    if (today && today.openingHours) {
        // Parse "HH:mm-HH:mm" format
        const timeParts = today.openingHours.split('-');
        if (timeParts.length === 2) {
            todayCloseText = timeParts[1];
        }
    }

    // Sort by date to ensure we find the nearest future opening
    const sortedHours = [...openingHours].sort((a, b) => a.date.localeCompare(b.date));

    for (const d of sortedHours) {
        if (!d.openingHours) continue;

        const timeParts = d.openingHours.split('-');
        if (timeParts.length !== 2) continue;

        const openTimeText = timeParts[0];

        if (d.date === currentDate && currentMinutes < timeStringToMinutes(openTimeText)) {
            nextOpen = { date: d.date, openTime: openTimeText, openText: openTimeText };
            break;
        }

        if (d.date > currentDate) {
            nextOpen = { date: d.date, openTime: openTimeText, openText: openTimeText };
            break;
        }
    }

    return { todayClose: today?.openingHours, todayCloseText, nextOpen };
}

function getDayName(dayOfWeek) {
    // Map day of week number to Danish day names (1=Monday, 7=Sunday)
    const dayNames = {
        1: 'Mandag',
        2: 'Tirsdag',
        3: 'Onsdag',
        4: 'Torsdag',
        5: 'Fredag',
        6: 'Lørdag',
        7: 'Søndag'
    };
    return dayNames[dayOfWeek] || null;
}

// show day date
function formatDayDate(dateStr) {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.getMonth() + 1;

    return `${day}/${month}`;
}

// Normalize date
function dateOnly(dateStr) {
    return dateStr.slice(0, 10);
}

function createOpeningHoursAccordion(pharmacy, statusText) {
    const openingHours = pharmacy.openingHours;
    if (!openingHours || openingHours.length === 0) return '';

    const today = new Date().toISOString().slice(0, 10);
    const todayData = openingHours.find(d => dateOnly(d.date) === today);

    const isOpen24h =
        todayData &&
        todayData.openingHours === "Døgnåbent";

    let status = "";
    let statusClass = "";
    let headerText = "";

    if (pharmacy.open || isOpen24h) {
        status = "Åbent";
        statusClass = "status-open";
        const helpText = pharmacy.closedHelpText || '';
        headerText = `
            <span class="${statusClass}">${status}</span>
            ${helpText ? `— ${helpText}` : ''}
        `;

        if (isOpen24h) {
            statusText = 'Åbent - Døgnåbent';
        }
    } else {
        status = "Lukket";
        statusClass = "status-closed";
        const helpText = pharmacy.openHelpText || '';
        headerText = `
            <span class="${statusClass}">${status}</span>
            ${helpText ? `— ${helpText}` : ''}
        `;
    }

    // Sort by date and show only current day + next 6 days
    const sorted = [...openingHours].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Extract unique dates in order
    const uniqueDates = [...new Set(sorted.map(d => dateOnly(d.date)))];

    // Find today in the date list
    const todayIndex = uniqueDates.indexOf(today);

    // Take 7 dates
    const datesToShow =
        todayIndex >= 0
            ? uniqueDates.slice(todayIndex, todayIndex + 7)
            : uniqueDates.slice(0, 7);

    // Filter records to 7 dates
    const daysToShow = sorted.filter(d =>
        datesToShow.includes(dateOnly(d.date))
    );
    // Merge consecutive time slots for the same date
    function mergeTimeSlots(slots) {
        if (!slots || slots.length === 0) return [];

        const merged = [];
        let current = { ...slots[0] };

        for (let i = 1; i < slots.length; i++) {
            const currentEnd = current.openingHours?.split('-')[1]?.trim();
            const nextStart = slots[i].openingHours?.split('-')[0]?.trim();

            if (currentEnd && nextStart && currentEnd === nextStart) {
                // Merge: update current slot's end time
                const nextEnd = slots[i].openingHours?.split('-')[1]?.trim();
                const currentStart = current.openingHours?.split('-')[0]?.trim();
                current.openingHours = `${currentStart}-${nextEnd}`;
            } else {
                // No merge: push current and start new
                merged.push(current);
                current = { ...slots[i] };
            }
        }
        merged.push(current);
        return merged;
    }

    // Group by date and merge slots
    const groupedByDate = {};
    daysToShow.forEach(day => {
        if (!groupedByDate[day.date]) {
            groupedByDate[day.date] = [];
        }
        groupedByDate[day.date].push(day);
    });

    const mergedDays = [];
    Object.keys(groupedByDate).forEach(date => {
        const slots = groupedByDate[date];
        const merged = mergeTimeSlots(slots);
        mergedDays.push(...merged);
    });

    let previousDate = null;
    const rows = mergedDays.map(day => {
        const name = getDayName(day.dayOfWeek);
        const formattedDate = formatDayDate(day.date);
        const showDayName = day.date !== previousDate;
        previousDate = day.date;

        let hours = "Lukket";

        if (day.openingHours) {
            if (day.openingHours === "00:00-00:00") {
                hours = "Døgnåbent";
            } else if (day.openingHours.includes('-')) {
                const timeParts = day.openingHours.split('-');
                if (timeParts.length === 2) {
                    hours = `${timeParts[0]} — ${timeParts[1]}`;
                }
            } else {
                hours = day.openingHours;
            }
        }

        const isToday = dateOnly(day.date) === today;

        return `
            <div class="opening-hours-row ${isToday ? "today" : ""}">
                <span class="day-of-week">${showDayName ? name : ''}</span>
                <span class="date">${formattedDate}</span>
                <span>${hours}</span>
            </div>`;
    }).join("");

    return `
        <div data-accordion-container class="accordion-component">
            <div class="accordion-items">
                <article class="accordion closed" data-accordion>
                    <div class="accordion-heading">
                        <button class="accordion-toggler closed" data-accordion-toggler aria-label="Fold element ud">
                            <div class="toggler-icons">
                                <span class="icon plus"><svg class="plus" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>
                                <span class="icon minus"><svg class="minus" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>
                            </div>
                            <p class="accordion-title style-base">${statusText}</p>
                        </button>
                    </div>
                    <div class="accordion-content hidden" aria-hidden data-accordion-content>
                        <div class="accordion-content-wrapper">
                            <div class="accordion-content-inner">
                                <p class="opening-hours-title">Åbningstider</p>
                                ${rows}
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    `;
}

function createPopupContentFromPharmacy(pharmacy) {
    const isOpen = isPharmacyOpen(pharmacy);
    const status = getOpeningDetails(pharmacy);
    const today = new Date().toISOString().slice(0, 10);

    let statusText = '';

    if (pharmacy.open) {
        if (pharmacy.closedHelpText) {
            statusText = `Åbent — ${pharmacy.closedHelpText}`;
        } else if (status.todayCloseText) {
            statusText = `Åbent til kl. ${status.todayCloseText}`;
        } else {
            statusText = 'Åbent';
        }
    } else if (status.nextOpen && status.nextOpen.date) {
        const today = new Date().toISOString().slice(0, 10);
        const { date, openText } = status.nextOpen;
        const nextOpenDate = date.slice(0, 10);
        
        // Current day before opening
        if (nextOpenDate === today) {
            statusText = `Lukket — Åbner kl. ${openText}`;
        } else {
            // Find the day of week for the next open date
            const nextOpenDay = pharmacy.openingHours.find(d => d.date.slice(0, 10) === nextOpenDate);
            if (nextOpenDay) {
                const dayName = getDayName(nextOpenDay.dayOfWeek);
                statusText = `Lukket — Åbner ${dayName.toLowerCase()} kl. ${openText}`;
            }
        }
    } else {
        statusText = 'Lukket';
    }

    function formatPhoneNumber(phone) {
        if (!phone) return '';

        // Remove prefix
        const withoutPrefix = phone.startsWith('+')
            ? phone.substring(3)
            : phone;

        // Add spacing
        return withoutPrefix
            .match(/.{1,2}/g)
            .join(' ');
    }

    const accordion = createOpeningHoursAccordion(pharmacy, statusText);
    const formattedPhoneNumber = formatPhoneNumber(pharmacy.phone);

    return `
        <div class="pharmacy-popup">
            <p class="pharmacy-popup-name">${pharmacy.name}</p>
            <button class="popup-close-btn" id="close-pharmacy-panel">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.667175" y="-2.28882e-05" width="16.027" height="0.942767" rx="0.471383" transform="rotate(45 0.667175 -2.28882e-05)" fill="black"/>
                    <rect x="0.000488281" y="11.3334" width="16.027" height="0.942767" rx="0.471383" transform="rotate(-45 0.000488281 11.3334)" fill="black"/>
                </svg>
            </button>
            <div class="pharmacy-popup-address">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12.0017 13.43C13.7248 13.43 15.1217 12.0331 15.1217 10.31C15.1217 8.58687 13.7248 7.19 12.0017 7.19C10.2785 7.19 8.88166 8.58687 8.88166 10.31C8.88166 12.0331 10.2785 13.43 12.0017 13.43Z" stroke="#215b66" stroke-width="1.5"/>
                    <path d="M3.62166 8.49C5.59166 -0.169998 18.4217 -0.159997 20.3817 8.5C21.5317 13.58 18.3717 17.88 15.6017 20.54C13.5917 22.48 10.4117 22.48 8.39166 20.54C5.63166 17.88 2.47166 13.57 3.62166 8.49Z" stroke="#215b66" stroke-width="1.5"/>
                </svg>
                <p class="address-wrapper">
                    <span>${pharmacy.street}</span>
                    <span> ${pharmacy.zipCode} ${pharmacy.city}</span>
                </p>
            </div>
            <div class="pharmacy-popup-contact">
                ${pharmacy.phone ? `<a href="tel:${pharmacy.phone}" class="pharmacy-popup-phone" aria-label="Call to:${pharmacy.phone}">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21.97 18.33C21.97 18.69 21.89 19.06 21.72 19.42C21.55 19.78 21.33 20.12 21.04 20.44C20.55 20.98 20.01 21.37 19.4 21.62C18.8 21.87 18.15 22 17.45 22C16.43 22 15.34 21.76 14.19 21.27C13.04 20.78 11.89 20.12 10.75 19.29C9.6 18.45 8.51 17.52 7.47 16.49C6.44 15.45 5.51 14.36 4.68 13.22C3.86 12.08 3.2 10.94 2.72 9.81C2.24 8.67 2 7.58 2 6.54C2 5.86 2.12 5.21 2.36 4.61C2.6 4 2.98 3.44 3.51 2.94C4.15 2.31 4.85 2 5.59 2C5.87 2 6.15 2.06 6.4 2.18C6.66 2.3 6.89 2.48 7.07 2.74L9.39 6.01C9.57 6.26 9.7 6.49 9.79 6.71C9.88 6.92 9.93 7.13 9.93 7.32C9.93 7.56 9.86 7.8 9.72 8.03C9.59 8.26 9.4 8.5 9.16 8.74L8.4 9.53C8.29 9.64 8.24 9.77 8.24 9.93C8.24 10.01 8.25 10.08 8.27 10.16C8.3 10.24 8.33 10.3 8.35 10.36C8.53 10.69 8.84 11.12 9.28 11.64C9.73 12.16 10.21 12.69 10.73 13.22C11.27 13.75 11.79 14.24 12.32 14.69C12.84 15.13 13.27 15.43 13.61 15.61C13.66 15.63 13.72 15.66 13.79 15.69C13.87 15.72 13.95 15.73 14.04 15.73C14.21 15.73 14.34 15.67 14.45 15.56L15.21 14.81C15.46 14.56 15.7 14.37 15.93 14.25C16.16 14.11 16.39 14.04 16.64 14.04C16.83 14.04 17.03 14.08 17.25 14.17C17.47 14.26 17.7 14.39 17.95 14.56L21.26 16.91C21.52 17.09 21.7 17.3 21.81 17.55C21.91 17.8 21.97 18.05 21.97 18.33Z" stroke="#215b66" stroke-width="1.5" stroke-miterlimit="10"/>
                        </svg>
                        ${formattedPhoneNumber}
                    </a>`
            : ''}
                ${pharmacy.email ? `<a href="mailto:${pharmacy.email}" class="pharmacy-popup-email" aria-label="Mail to ${pharmacy.email}">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="#215b66" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="#215b66" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        ${pharmacy.email}
                    </a>` : ''}
            </div>
            ${accordion}
            ${pharmacy?.filters?.some(f => f.value === 'wheelchairAccessibleEntrance')
                ? `<p class="wheelchair-accessible-entrance">Apoteket er fysisk tilgængeligt for handicappede.</p>`
                : ''}
            <div class="pharmacy-popup-actions">
                <a href="/apoteker/${pharmacy.fullSlug}" class="pharmacy-popup-link button button-primary">
                    Se mere om apoteket
                    <svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 1L6 5L2 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="square"/>
                    </svg>

                </a>
            </div>
        </div>`;
}

// Listen for Google Maps ready event
// Handle race condition: check if map already initialized
if (typeof map !== 'undefined' && map && typeof google !== 'undefined') {
    initPharmacyMap();
} else {
    window.addEventListener('googleMapReady', initPharmacyMap);
}